import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import WelcomeScreen from './WelcomeScreen';
import SpecificCourse from './Courses';
import Login from './Login';
import Dashboard from './Dashboard';
import ExamList from './ExamList';
import ExamView from './ExamView';
import TopicSelector from './TopicSelector';
import Resources from './Resources';
import { useExamData } from './useExamData';
import { Exam, Question } from './examData';

const AppWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Navbar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #00205B;
  padding: 0 32px;
  height: 56px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: sticky;
  top: 0;
  z-index: 100;

  @media (max-width: 600px) {
    padding: 0 12px;
    height: 48px;
  }
`;

const NavbarBrand = styled.span`
  font-size: 1.3rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.5px;
  cursor: pointer;

  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const NavbarTabs = styled.div`
  display: flex;
  gap: 4px;
`;

interface TabProps {
    active?: boolean;
}

const Tab = styled.button<TabProps>`
  background: none;
  border: none;
  color: ${props => (props.active ? '#ffffff' : '#ffffffaa')};
  font-family: 'Roboto', sans-serif;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 16px 20px;
  cursor: pointer;
  border-bottom: 3px solid ${props => (props.active ? '#ffffff' : 'transparent')};
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: #ffffff;
  }

  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 12px 10px;
  }
`;

const NavbarAuth = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

interface AuthButtonProps {
    primary?: boolean;
}

const AuthButton = styled.button<AuthButtonProps>`
  background: ${props => (props.primary ? '#ffffff' : 'transparent')};
  color: ${props => (props.primary ? '#00205B' : '#ffffff')};
  border: 1px solid ${props => (props.primary ? '#ffffff' : '#ffffff88')};
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Roboto', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.primary ? '#ffffffdd' : '#ffffff22')};
  }
`;

type ActiveView = 'welcome' | 'courses' | 'login' | 'dashboard' | 'examList' | 'timedExamList' | 'examView' | 'topicSelector' | 'resources';

const UserAvatar = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #ffffff;
    color: #00205B;
    font-size: 0.8rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Roboto', sans-serif;
`;

const LoadingScreen = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background-color: #f8fafc;
    color: #00205B;
    font-size: 1.1rem;
    font-weight: 500;
    font-family: 'Roboto', sans-serif;
`;

class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
    constructor(props: {children: React.ReactNode}) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <LoadingScreen>
                    <div style={{ textAlign: 'center' }}>
                        <p>Something went wrong.</p>
                        <button onClick={() => window.location.reload()} style={{
                            marginTop: 12, padding: '10px 24px', background: '#00205B',
                            color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
                            fontSize: '0.95rem'
                        }}>
                            Reload
                        </button>
                    </div>
                </LoadingScreen>
            );
        }
        return this.props.children;
    }
}

const App: React.FC = () => {
    const { exams, topics, loading } = useExamData();
    const [activeView, setActiveViewRaw] = useState<ActiveView>('welcome');
    const [selectedCourseId, setSelectedCourseId] = useState<string>('');
    const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
    const [examBackView, setExamBackView] = useState<ActiveView>('examList');
    const [user, setUser] = useState<User | null>(null);
    const [timedMode, setTimedMode] = useState(false);

    useEffect(() => {
        const validateRiceEmail = async (session: { user: User } | null) => {
            if (session?.user?.email && !session.user.email.endsWith('@rice.edu')) {
                await supabase.auth.signOut();
                setUser(null);
                return;
            }
            setUser(session?.user ?? null);
        };

        supabase.auth.getSession().then(({ data: { session } }) => {
            validateRiceEmail(session);
        });
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            validateRiceEmail(session);
        });
        return () => subscription.unsubscribe();
    }, []);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        setUser(null);
        navigateTo('welcome');
    };

    const navigateTo = useCallback((view: ActiveView, replace = false) => {
        setActiveViewRaw(view);
        if (replace) {
            window.history.replaceState({ view }, '', '');
        } else {
            window.history.pushState({ view }, '', '');
        }
    }, []);

    useEffect(() => {
        window.history.replaceState({ view: 'welcome' }, '', '');
        const handlePopState = (e: PopStateEvent) => {
            if (e.state && e.state.view) {
                setActiveViewRaw(e.state.view as ActiveView);
            } else {
                setActiveViewRaw('welcome');
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const setActiveView = navigateTo;

    const handleCourseAction = (courseId: string, action: string) => {
        if (action === 'exam') {
            setSelectedCourseId(courseId);
            navigateTo('examList');
        } else if (action === 'topic') {
            setSelectedCourseId(courseId);
            navigateTo('topicSelector');
        } else if (action === 'timed') {
            setSelectedCourseId(courseId);
            navigateTo('timedExamList');
        } else if (action === 'resources') {
            setSelectedCourseId(courseId);
            navigateTo('resources');
        }
    };

    const handleSelectExam = (exam: Exam) => {
        setSelectedExam(exam);
        setTimedMode(false);
        setExamBackView('examList');
        navigateTo('examView');
    };

    const handleSelectTimedExam = (exam: Exam) => {
        setSelectedExam(exam);
        setTimedMode(true);
        setExamBackView('timedExamList');
        navigateTo('examView');
    };

    const handleBuildTopicExam = (questions: Question[], name: string) => {
        const customExam: Exam = {
            id: `custom-${Date.now()}`,
            courseId: selectedCourseId,
            title: name,
            semester: '',
            year: 0,
            questions,
        };
        setSelectedExam(customExam);
        setTimedMode(false);
        setExamBackView('topicSelector');
        navigateTo('examView');
    };

    const handleDashboardSelectExam = (exam: Exam) => {
        setSelectedExam(exam);
        setTimedMode(false);
        setExamBackView('dashboard');
        navigateTo('examView');
    };

    const handleRetakeExam = async (exam: Exam) => {
        if (user) {
            await supabase
                .from('user_answers')
                .delete()
                .eq('user_id', user.id)
                .eq('exam_id', exam.id);
        }
        setSelectedExam({ ...exam });
        setTimedMode(false);
        setExamBackView('dashboard');
        navigateTo('examView');
    };

    const isMainTab = (tab: string) => {
        if (tab === 'welcome') return activeView === 'welcome';
        if (tab === 'courses') return ['courses', 'examList', 'timedExamList', 'examView', 'topicSelector', 'resources'].includes(activeView);
        if (tab === 'dashboard') return activeView === 'dashboard';
        return false;
    };

    if (loading) {
        return <LoadingScreen>Loading...</LoadingScreen>;
    }

    return (
        <AppWrapper>
            <Navbar>
                <NavbarBrand onClick={() => setActiveView('welcome')}>Rice Exams</NavbarBrand>
                <NavbarTabs>
                    <Tab
                        active={isMainTab('welcome')}
                        onClick={() => setActiveView('welcome')}
                    >
                        Welcome
                    </Tab>
                    {user && (
                        <Tab
                            active={isMainTab('courses')}
                            onClick={() => setActiveView('courses')}
                        >
                            Courses
                        </Tab>
                    )}
                    {user && (
                        <Tab
                            active={isMainTab('dashboard')}
                            onClick={() => setActiveView('dashboard')}
                        >
                            Dashboard
                        </Tab>
                    )}
                </NavbarTabs>
                <NavbarAuth>
                    {user ? (
                        <>
                            <UserAvatar>{(user.email || '').slice(0, 2).toUpperCase()}</UserAvatar>
                            <AuthButton onClick={handleLogout}>Log Out</AuthButton>
                        </>
                    ) : (
                        <AuthButton primary onClick={() => setActiveView('login')}>Sign In</AuthButton>
                    )}
                </NavbarAuth>
            </Navbar>

            {activeView === 'welcome' && <WelcomeScreen onNavigate={(v) => setActiveView(v as ActiveView)} />}
            {activeView === 'login' && <Login />}
            {!user && activeView !== 'welcome' && activeView !== 'login' && <Login />}
            {user && activeView === 'courses' && (
                <SpecificCourse onCourseAction={handleCourseAction} />
            )}
            {user && activeView === 'dashboard' && (
                <Dashboard
                    userId={user.id}
                    exams={exams}
                    topics={topics}
                    onSelectExam={handleDashboardSelectExam}
                    onRetakeExam={handleRetakeExam}
                />
            )}
            {user && activeView === 'examList' && (
                <ExamList
                    courseId={selectedCourseId}
                    exams={exams}
                    onBack={() => setActiveView('courses')}
                    onSelectExam={handleSelectExam}
                />
            )}
            {user && activeView === 'timedExamList' && (
                <ExamList
                    courseId={selectedCourseId}
                    exams={exams}
                    onBack={() => setActiveView('courses')}
                    onSelectExam={handleSelectTimedExam}
                    timedMode
                />
            )}
            {user && activeView === 'topicSelector' && (
                <TopicSelector
                    courseId={selectedCourseId}
                    exams={exams}
                    topics={topics}
                    onBack={() => setActiveView('courses')}
                    onBuildExam={handleBuildTopicExam}
                />
            )}
            {user && activeView === 'resources' && (
                <Resources
                    courseId={selectedCourseId}
                    onBack={() => setActiveView('courses')}
                />
            )}
            {user && activeView === 'examView' && selectedExam && (
                <ExamView
                    exam={selectedExam}
                    onBack={() => setActiveView(examBackView)}
                    userId={user.id}
                    timed={timedMode}
                />
            )}
        </AppWrapper>
    );
};

const AppWithErrorBoundary: React.FC = () => (
    <ErrorBoundary>
        <App />
    </ErrorBoundary>
);

export default AppWithErrorBoundary;
